/**
 * The text of a document chosen for review, read in the browser.
 *
 * The review keeps this text — not the file — as the document of record: the
 * agent reads it and the viewer highlights passages in it, so both always see
 * the same characters. PDFs are read with pdf.js (already the app's PDF
 * renderer), Word files from their `word/document.xml` with the shared zip
 * reader (tracked deletions left out), text and Markdown as they are. A scan
 * with no text layer yields nothing, which the page says plainly.
 *
 * @module components/apps/contractIntelligence/extractDocumentText
 */
import { readDocxText } from "@amoreg/shared/docxText";

export { docxXmlToText } from "@amoreg/shared/docxText";

export const DOCX_MEDIA_TYPE = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export const ACCEPTED_DOCUMENTS = ".pdf,.docx,.txt,.md,application/pdf,text/plain,text/markdown," + DOCX_MEDIA_TYPE;

/** The media type a file is reviewed as, from its type or its extension; null when unsupported. */
export function reviewMediaType(file: { readonly name: string; readonly type: string }): string | null {
  const name = file.name.toLowerCase();
  if (file.type === "application/pdf" || name.endsWith(".pdf")) return "application/pdf";
  if (file.type === DOCX_MEDIA_TYPE || name.endsWith(".docx")) return DOCX_MEDIA_TYPE;
  if (file.type === "text/markdown" || name.endsWith(".md") || name.endsWith(".markdown")) return "text/markdown";
  if (file.type === "text/plain" || name.endsWith(".txt")) return "text/plain";
  return null;
}

export type PdfTextItem = {
  readonly str?: string;
  readonly hasEOL?: boolean;
  readonly transform?: ReadonlyArray<number>;
  readonly height?: number;
};

/** A clause or list marker that starts its own line: "4.", "4.1", "(a)", "(iv)", "•". */
const CLAUSE_START = /^(?:\(?\d+(?:\.\d+)*[.)]?|\([a-z]{1,4}\)|[•▪◦\-–])\s/i;

/**
 * One PDF page's text as paragraphs. pdf.js ends every visual line; a
 * contract's sentences run across several, so lines are joined with a space
 * unless the gap below the previous line is paragraph-sized or the new line
 * opens a numbered clause. The viewer then shows clauses as written rather
 * than as the PDF happened to wrap them.
 */
export function pdfPageText(items: ReadonlyArray<PdfTextItem>): string {
  const lines: Array<{ text: string; y: number; size: number }> = [];
  let text = "";
  let y: number | null = null;
  let size = 0;
  const close = () => {
    if (text.trim()) lines.push({ text: text.replace(/[ \t ]+/g, " ").trim(), y: y ?? 0, size: size || 10 });
    text = "";
    y = null;
    size = 0;
  };
  for (const item of items) {
    if (typeof item.str !== "string") continue;
    const [a = 0, b = 0, , , , itemY] = item.transform ?? [];
    if (y === null && itemY !== undefined) y = itemY;
    size = Math.max(size, Math.hypot(a, b) || item.height || 0);
    text += item.str;
    if (item.hasEOL) close();
  }
  close();
  let out = "";
  lines.forEach((line, index) => {
    const previous = lines[index - 1];
    if (!previous) {
      out = line.text;
      return;
    }
    const gap = Math.abs(previous.y - line.y);
    const paragraph = gap > Math.max(previous.size, line.size) * 1.45;
    out += paragraph ? "\n\n" : CLAUSE_START.test(line.text) ? "\n" : " ";
    out += line.text;
  });
  return out;
}

/** The longest PDF a review reads; a longer one is refused rather than reviewed in part. */
export const MAX_PDF_PAGES = 400;

/** A refusal whose sentence is already the one to show. */
class UnreadableDocument extends Error {}

export const tooManyPagesMessage = (pages: number) =>
  `This PDF has ${pages} pages; a review reads at most ${MAX_PDF_PAGES}. Split it and add the parts, so no page goes unreviewed.`;

async function pdfText(bytes: ArrayBuffer): Promise<string> {
  const pdfjs = await import("pdfjs-dist");
  const { default: workerUrl } = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
  if (!pdfjs.GlobalWorkerOptions.workerSrc) pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
  const document = await pdfjs.getDocument({ data: new Uint8Array(bytes) }).promise;
  try {
    // A partial read could conclude "no gaps" about pages it never saw.
    if (document.numPages > MAX_PDF_PAGES) throw new UnreadableDocument(tooManyPagesMessage(document.numPages));
    const pages: Array<string> = [];
    for (let number = 1; number <= document.numPages; number += 1) {
      const page = await document.getPage(number);
      const content = await page.getTextContent();
      pages.push(pdfPageText(content.items as ReadonlyArray<PdfTextItem>));
      page.cleanup();
    }
    return pages.filter((page) => page.length > 0).join("\n\n");
  } finally {
    await document.destroy();
  }
}

const docxText = (bytes: ArrayBuffer): Promise<string> => readDocxText(new Uint8Array(bytes));

async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export interface ExtractedDocument {
  readonly name: string;
  readonly mediaType: string;
  readonly byteSize: number;
  readonly sha256: string;
  readonly text: string;
}

/** Read a file for review; rejects with a sentence a person can act on. */
export async function extractDocumentText(file: File): Promise<ExtractedDocument> {
  const mediaType = reviewMediaType(file);
  if (!mediaType) throw new Error("Only PDF, Word (.docx), text and Markdown files can be reviewed.");
  if (file.size > 25 * 1024 * 1024) throw new Error("This file is larger than 25 MB.");
  const bytes = await file.arrayBuffer();
  let text: string;
  try {
    text =
      mediaType === "application/pdf"
        ? await pdfText(bytes)
        : mediaType === DOCX_MEDIA_TYPE
          ? await docxText(bytes)
          : new TextDecoder().decode(bytes);
  } catch (cause) {
    if (cause instanceof UnreadableDocument) throw cause;
    throw new Error("This file could not be read. It may be damaged or password-protected.");
  }
  text = text.replace(/\r\n?/g, "\n").replace(/\u0000/g, "").trim();
  if (text.length < 20) {
    throw new Error("No text could be read from this file. A scanned document needs text recognition first.");
  }
  return { name: file.name, mediaType, byteSize: file.size, sha256: await sha256Hex(bytes), text };
}
